import { ReactElement } from "react";
import { ExclamationCircleOutlined, CopyOutlined } from "@ant-design/icons";
import LayoutCard from "../../../shared-ui-components/LayoutCard";
import ProgRing from "../../../shared-ui-components/ProgRing";
import ToolTip from "../../../shared-ui-components/ToolTip";
import {
  getVerdictbyFlag,
  summaryTitlesForVerdicts,
} from "../../../logic/business-rules";
import { toHumanReadableSize, toKebabCase } from "../../../util/general";
import { UrlPrimaryViewModel } from "../../../logic/url-scan-data-processing";
import "./UrlOverviewCard.scss";

interface UrlOverviewCardProps {
  primary: UrlPrimaryViewModel;
  overallRating: number;
}

function copyToClipboard(value: string) {
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

export default function UrlOverviewCard({
  primary,
  overallRating,
}: UrlOverviewCardProps): ReactElement {
  if (!primary.requestedUrl) {
    return (
      <div className="top-summary-card-error">
        <h3>No summary available</h3>
        <p>{primary.error?.message ?? "This URL could not be scanned."}</p>
      </div>
    );
  }

  if (primary.error && !primary.analysisForReuse) {
    return (
      <LayoutCard className="top-summary unsupported">
        {{
          content: (
            <div className="flex-row-with-vertical-alignment">
              <div className="risk-rating-gauge">
                <ProgRing value={-1} />
              </div>
              <div className="summary-divider" />
              <div className="top-summary-card-right-part">
                <div className="flex-space-between align-items-center">
                  <div className="verdict-summary unsupported">
                    This URL could not be reached, so no analysis is
                    available.&nbsp;
                    <ToolTip
                      children={
                        <span className="verdict-tooltip-icon">
                          <ExclamationCircleOutlined className="exclamation-circle" />
                        </span>
                      }
                      content={primary.error.message}
                    />
                  </div>
                  <div className="indicator-verdict-label unsupported">
                    Unknown
                  </div>
                </div>
                <div className="url-requested-address">
                  <span className="url-address-label">Requested URL</span>
                  <span className="url-address-value">
                    {primary.requestedUrl}
                  </span>
                </div>
              </div>
            </div>
          ),
        }}
      </LayoutCard>
    );
  }

  const rating = primary.rating ?? overallRating;
  const ratingFlag = primary.ratingFlag ?? "Not Found";
  const verdict = getVerdictbyFlag(ratingFlag);
  const verdictSummary = summaryTitlesForVerdicts[verdict];

  return (
    <LayoutCard className={`top-summary ${toKebabCase(verdict)}`}>
      {{
        content: (
          <div className="flex-row-with-vertical-alignment">
            <div className="risk-rating-gauge">
              <ProgRing value={verdict === "Unsupported" ? -1 : rating} />
            </div>
            <div className="summary-divider" />
            <div className="top-summary-card-right-part">
              <div className="flex-space-between align-items-center">
                <div className={`verdict-summary ${toKebabCase(verdict)}`}>
                  {verdictSummary}&nbsp;
                  {primary.description && (
                    <ToolTip
                      children={
                        <span className="verdict-tooltip-icon">
                          <ExclamationCircleOutlined className="exclamation-circle" />
                        </span>
                      }
                      content={primary.description}
                    />
                  )}
                </div>
                <div className={`indicator-verdict-label ${toKebabCase(verdict)}`}>
                  {verdict}
                </div>
              </div>
              <div className="url-requested-address">
                <span className="url-address-label">Requested URL</span>
                <span className="url-address-value">{primary.requestedUrl}</span>
              </div>
              {primary.sha256 && (
                <div
                  className="flex-row-with-vertical-alignment"
                  style={{ flexDirection: "row" }}
                >
                  <span
                    className="icon-with-background clickable"
                    onClick={() => copyToClipboard(primary.sha256 as string)}
                  >
                    <CopyOutlined />
                  </span>
                  <div className="sha256-info">
                    <div className="sha256-label">SHA256</div>
                    <div className="sha256-text">{primary.sha256}</div>
                  </div>
                </div>
              )}
              <div className="top-summary-card-inner-cards-wrapper pills url-overview-pills">
                <div className="pill-div">
                  <div className="pill">{primary.httpStatus ?? "-"}</div>
                  HTTP STATUS
                </div>
                <div className="pill-div">
                  <div className="pill">{primary.contentType ?? "-"}</div>
                  CONTENT TYPE
                </div>
                <div className="pill-div">
                  <div className="pill">
                    {primary.size !== null
                      ? toHumanReadableSize(primary.size)
                      : "-"}
                  </div>
                  SIZE
                </div>
                <div className="pill-div">
                  <div className="pill">{primary.scanTimeMs ?? "-"} ms</div>
                  ANALYSIS DURATION
                </div>
                <div className="pill-div">
                  <div className="pill">
                    {primary.timestamp
                      ? `${new Date(primary.timestamp).toLocaleDateString("en-GB")} ${new Date(
                          primary.timestamp,
                        ).toLocaleTimeString()}`
                      : "-"}
                  </div>
                  TIMESTAMP
                </div>
              </div>
            </div>
          </div>
        ),
      }}
    </LayoutCard>
  );
}
